import {
  Button,
  Input,
  MapPin,
  Plus,
  Select,
  Trash2,
} from "@ews-admin/global-design-system";
import { useEffect } from "react";
import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ResidencyEnum } from "../../../types/doctor.definition";
import {
  AcceptedCountriesEnum,
  Location,
} from "../../../types/user.definition";
import { getRegionsByCountry } from "../../../utils/locations.utils";

interface LocationFormProps<
  T extends { locations: Location[] } = { locations: Location[] },
> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  errors?: FieldErrors<T>;
  showResidency?: boolean;
  showPrimary?: boolean;
  /** If provided, only display the location at this index from the array */
  singleLocationIndex?: number;
  /** MED-1687: mark the street address required (the patient schema requires it) */
  addressRequired?: boolean;
}

export const LocationForm = <
  T extends { locations: Location[] } = { locations: Location[] },
>({
  control,
  setValue,
  errors,
  showResidency = true,
  showPrimary = true,
  singleLocationIndex,
  addressRequired = false,
}: LocationFormProps<T>) => {
  const isSingleLocationMode = singleLocationIndex !== undefined;
  const { t } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "locations" as never,
  });

  const watchLocations = (useWatch({
    control: control as unknown as Control<{ locations: Location[] }>,
    name: "locations",
    defaultValue: [] as Location[],
  }) || []) as Location[];

  // Ensure that if there's only one location, it's set as primary
  useEffect(() => {
    if (showPrimary && watchLocations.length === 1) {
      const location = watchLocations[0];
      if (!location.isPrimary) {
        const updatedLocations = [
          {
            ...location,
            city: location.city ?? "",
            zip: location.zip ?? "",
            isPrimary: true,
          },
        ];
        setValue("locations" as never, updatedLocations as never, {
          shouldValidate: true,
          shouldDirty: false, // Don't mark as dirty on initialization
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchLocations.length, showPrimary]);

  const handleAddLocation = () => {
    const currentLocations = watchLocations || [];
    const willBeOnlyLocation = currentLocations.length === 0;

    const newLocation: Location = {
      address: "",
      state: "",
      city: "",
      zip: "",
      country: AcceptedCountriesEnum.SENEGAL,
      residency: ResidencyEnum.OTHER,
      isPrimary: willBeOnlyLocation, // Set as primary if it's the first location
    };

    const updatedLocations = [...currentLocations, newLocation];

    // If after adding there's only one location, ensure it's primary
    if (showPrimary && updatedLocations.length === 1) {
      updatedLocations[0] = {
        ...updatedLocations[0],
        isPrimary: true,
      };
    }

    append(newLocation as never);
    setValue("locations" as never, updatedLocations as never, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleRemoveLocation = (index: number) => {
    const locationsAfterRemoval = [...watchLocations];
    locationsAfterRemoval.splice(index, 1);

    // If we removed the primary location and there are still locations left,
    // set the first one as primary
    let updatedLocations = locationsAfterRemoval;
    if (updatedLocations.length > 0 && showPrimary) {
      const hadPrimary = watchLocations[index]?.isPrimary;
      const hasPrimaryLeft = updatedLocations.some((loc) => loc.isPrimary);

      if (hadPrimary || !hasPrimaryLeft) {
        updatedLocations = updatedLocations.map((loc, i) =>
          i === 0 ? { ...loc, isPrimary: true } : loc,
        );
      }
    }

    remove(index);
    setValue("locations" as never, updatedLocations as never, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleLocationFieldChange = (
    index: number,
    field: keyof Location,
    value: string | boolean | undefined,
  ) => {
    // MED-1687: set the field itself — setting the whole array validated it but left a stale
    // message on the field ("Champ obligatoire" under a region that was just chosen)
    setValue(`locations.${index}.${field}` as never, value as never, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleLocationPrimaryChange = (index: number, checked: boolean) => {
    if (!showPrimary) return;

    // Don't allow unsetting if it's the only location
    if (!checked && watchLocations.length === 1) {
      return;
    }

    // Create new location objects with updated isPrimary values
    const updatedLocations = watchLocations.map((loc, i) => {
      if (checked) {
        // If setting a location as primary, only the selected one should be true
        return {
          ...loc,
          isPrimary: i === index,
        };
      } else {
        // If unchecking, only update the selected location
        return i === index
          ? {
              ...loc,
              isPrimary: false,
            }
          : loc;
      }
    });

    setValue("locations" as never, updatedLocations as never, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // Filter fields to display based on mode
  const fieldsToDisplay = isSingleLocationMode
    ? fields.filter((_, index) => index === singleLocationIndex)
    : fields;

  return (
    <div className="space-y-6">
      {!isSingleLocationMode && (
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-3">
            <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-success-100">
              <MapPin className="w-4 h-4 text-success-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900">
              {t("profile.addressInformation")}
            </h3>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleAddLocation}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t("profile.addLocation")}</span>
          </Button>
        </div>
      )}

      {/* Locations List */}
      <div className="space-y-6">
        {fieldsToDisplay.map((field) => {
          // Get the actual index in the original locations array
          const index = isSingleLocationMode
            ? singleLocationIndex!
            : fields.findIndex((f) => f.id === field.id);
          const location = watchLocations[index] || {};
          const locationErrors = (
            errors?.locations as unknown as
              | Array<FieldErrors<Location>>
              | undefined
          )?.[index];

          return (
            <div
              key={`location-${index}`}
              className="p-6 space-y-5 bg-white rounded-lg border shadow-sm border-neutral-200"
            >
              {/* Header with title and badge */}
              {!isSingleLocationMode && (
                <div className="flex justify-between items-center">
                  <div className="flex gap-3 items-center">
                    <h4 className="text-lg font-semibold text-neutral-900">
                      {t("profile.location")} {index + 1}
                    </h4>
                    {location.isPrimary && showPrimary && (
                      <span className="inline-flex gap-1 items-center px-3 py-1 text-sm font-medium rounded-full border bg-primary-100 text-primary-700 border-primary-200">
                        <span className="text-primary-600">★</span>
                        {t("profile.isPrimary")}
                      </span>
                    )}
                  </div>
                  {watchLocations.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveLocation(index)}
                      className="text-error-600 hover:text-error-700 hover:bg-error-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}

              {/* First row: Residency Type and Country */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {showResidency && (
                  <div className="space-y-1">
                    <Select
                      size="md"
                      label={t("profile.residencyType")}
                      value={location.residency || ResidencyEnum.HOME}
                      onChange={(value) =>
                        handleLocationFieldChange(
                          index,
                          "residency",
                          value as string,
                        )
                      }
                      options={[
                        {
                          value: ResidencyEnum.HOME,
                          label: t("profile.residencyTypes.home"),
                        },
                        {
                          value: ResidencyEnum.SECOND_HOME,
                          label: t("profile.residencyTypes.secondHome"),
                        },
                        {
                          value: ResidencyEnum.OTHER,
                          label: t("profile.residencyTypes.other"),
                        },
                      ]}
                    />
                  </div>
                )}

                <div className="space-y-1">
                  <Select
                    size="md"
                    label={t("profile.country")}
                    value={location.country || AcceptedCountriesEnum.SENEGAL}
                    onChange={(value) =>
                      handleLocationFieldChange(
                        index,
                        "country",
                        value as string,
                      )
                    }
                    options={[
                      {
                        value: AcceptedCountriesEnum.SENEGAL,
                        label: t("common.countries.senegal"),
                      },
                      {
                        value: AcceptedCountriesEnum.CHAD,
                        label: t("common.countries.chad"),
                      },
                    ]}
                    error={locationErrors?.country?.message}
                  />
                </div>
              </div>

              {/* Second row: Address and Region */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Input
                  required={addressRequired}
                  label={t("profile.streetAddress")}
                  name={`locations.${index}.address`}
                  value={location.address || ""}
                  onChange={(e) =>
                    handleLocationFieldChange(index, "address", e.target.value)
                  }
                  placeholder={t("profile.streetAddressPlaceholder")}
                  leftIcon={<MapPin size={16} />}
                  error={locationErrors?.address?.message}
                />

                <div className="space-y-1">
                  <Select
                    // MED-1687: every location schema requires the region; it had no asterisk
                    required
                    size="md"
                    label={t("profile.region")}
                    placeholder={t("profile.selectRegion")}
                    value={location.state || ""}
                    onChange={(value) =>
                      handleLocationFieldChange(index, "state", value as string)
                    }
                    options={getRegionsByCountry(
                      location.country || AcceptedCountriesEnum.SENEGAL,
                    ).map((region) => ({
                      value: region,
                      label: region,
                    }))}
                    error={locationErrors?.state?.message}
                  />
                </div>
              </div>

              {/* Third row: Access Information */}
              <Input
                size="md"
                label={t("profile.accessInformation")}
                name={`locations.${index}.accessInformation`}
                value={location.accessInformation || ""}
                onChange={(e) =>
                  handleLocationFieldChange(
                    index,
                    "accessInformation",
                    e.target.value,
                  )
                }
                placeholder={t("profile.accessInformationPlaceholder")}
                leftIcon={<MapPin size={16} />}
              />

              {/* Primary Address Toggle - Only show if multiple locations and not in single location mode */}
              {showPrimary &&
                watchLocations.length > 1 &&
                !isSingleLocationMode && (
                  <div className="flex gap-2 items-center pt-4 border-t border-neutral-200">
                    <input
                      type="checkbox"
                      id={`isPrimary-${index}`}
                      checked={location.isPrimary || false}
                      onChange={(e) =>
                        handleLocationPrimaryChange(index, e.target.checked)
                      }
                      className="w-4 h-4 bg-white rounded text-primary-600 border-neutral-300 focus:ring-primary-500 focus:ring-2"
                    />
                    <label
                      htmlFor={`isPrimary-${index}`}
                      className="text-sm font-medium cursor-pointer text-neutral-700"
                    >
                      {t("profile.setPrimary")}
                    </label>
                  </div>
                )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
